/**
 * The tests a TestIt invitation asks for, where the citizen accepts it.
 * (D3 · 4 Oct 2026)
 *
 * A TestIt invitation is accepted as TESTS: no journey is chosen. This is what
 * the accept surfaces show in the chooser's place — each asked test, with the
 * result that will be shared when one is already banked ("already done — your
 * result from 3 Oct will be shared") or that it is still to take — and what
 * accepting shares, which is the server's answer and never a guess from the
 * journey. Shared by the dashboard review dialog and `/accept-invite`.
 */
import { CheckCircle2, Circle } from '@tonnie/assets';
import { useLocale, useTranslation } from '@tonnie/locale';
import { testitAcceptScope, testitAskedTestStatus, testitTestLabel } from '@tonnie/onboarding';
import type { AcceptAskedTest } from '@tonnie/sdk';

export function InvitationAskedTests({
  tests,
  companyName,
  completed,
  sharesCheckup,
}: {
  tests: AcceptAskedTest[];
  companyName: string;
  /** Every asked test already has a result: nothing is left to take. */
  completed: boolean;
  /** The relationship holds the CheckUp once accepted — the server's fact. */
  sharesCheckup: boolean;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  return (
    <section
      className="space-y-3"
      aria-labelledby="invitation-asked-tests-label"
      data-testid="invitation-journey-choice"
      data-choice-state="tests"
      data-tests-complete={completed}
    >
      <h3 id="invitation-asked-tests-label" className="text-sm font-medium">
        {t('invitations.testit.listLabel', { company: companyName })}
      </h3>
      <ul className="space-y-2" data-testid="invitation-asked-tests" data-test-count={tests.length}>
        {tests.map((test) => (
          <li
            key={test.testId}
            className="flex items-start gap-3 rounded-xl border border-border p-3"
            data-testid={`invitation-asked-test-${test.testId}`}
            data-test-done={test.done}
          >
            {test.done ? (
              <CheckCircle2
                className="mt-0.5 h-5 w-5 shrink-0 text-tone-green"
                aria-hidden="true"
              />
            ) : (
              <Circle
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            )}
            <span className="min-w-0 space-y-0.5">
              <span className="block text-sm font-semibold text-foreground">
                {testitTestLabel(test.testId, t)}
              </span>
              <span className="block text-xs text-muted-foreground leading-relaxed">
                {testitAskedTestStatus(t, test, formatDate)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <p
        className="text-xs text-muted-foreground leading-relaxed"
        data-testid="invitation-testit-scope"
      >
        {testitAcceptScope(t, { company: companyName, sharesCheckup })}
      </p>
    </section>
  );
}
